import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Empty, Select, Space, Table, Tabs, Tag, Tooltip } from 'antd';
import {
  BankOutlined,
  CalculatorOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CodeOutlined,
  CopyOutlined,
  EditOutlined,
  ExportOutlined,
  EyeOutlined,
  FileExcelOutlined,
  FileDoneOutlined,
  FilePdfOutlined,
  LinkOutlined,
  QrcodeOutlined,
  UploadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { ContentBackground, ContentBody, ContentHeader, ContentWrapper } from '@/components/content';
import { HowToCard } from '@/components/how-to-card';
import { withPrivateRoute } from '@/components/hoc/withPrivateRoute';
import { ContainerFluid, Section } from '@/components/layouts';
import RootLayout from '@/components/layouts/RootLayout';
import { ROLES_PERMISSIONS } from '@/constants';
import { useCompaniesDropdown } from '@/hooks/queries/use-companies-dropdown';
import { API } from '@/services/api.service';
import { useAuthStore } from '@/store/useAuthStore';
import AbJournalModal from './_components/AbJournalModal';
import AccountsPanel from './_components/AccountsPanel';
import CalculationsPanel from './_components/CalculationsPanel';
import DriveStatusLine from './_components/DriveStatusLine';
import ImportModal from './_components/ImportModal';
import PaymentSlipModal from './_components/PaymentSlipModal';
import ReturnDrawer from './_components/ReturnDrawer';
import {
  CATEGORIES,
  CATEGORY_COLORS,
  STATUSES,
  STATUS_COLORS,
  date,
  errorText,
  money,
  saveBlob,
} from './_components/format';

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

/**
 * Tax returns and whether they are paid, per project, from the imported bank
 * statements. Overdue unpaid returns come first and are red.
 *
 * @returns {JSX.Element}
 */
function TaxReturnsContent() {
  const { t } = useTranslation(['taxReturns', 'menus']);
  const userRoles = useAuthStore((state) => state.user?.roles) || [];
  const canManage = ROLES_PERMISSIONS.TAX_RETURNS.MANAGE.requiredRoles.some((r) => userRoles.includes(r));

  // The company lives in the address, so a link or a reload opens the same list.
  const [searchParams, setSearchParams] = useSearchParams();
  const company = searchParams.get('company');
  const setCompany = (value) => setSearchParams(value ? { company: value } : {}, { replace: true });
  const [filters, setFilters] = useState({});
  const [tab, setTab] = useState('returns');
  const [importOpen, setImportOpen] = useState(false);
  const [openId, setOpenId] = useState(null);
  // PDPO returns picked for a journal file for AB Soft.
  const [selected, setSelected] = useState([]);
  const [abOpen, setAbOpen] = useState(false);
  const [slipOf, setSlipOf] = useState(null);

  const { data: companies } = useCompaniesDropdown({ queryParams: {} });
  useEffect(() => {
    if (!company && companies?.options?.length === 1) setCompany(companies.options[0].value);
  }, [companies, company]);

  const { data, isLoading, refetch, error } = useQuery({
    queryKey: ['tax-returns', company, filters],
    queryFn: () => API.taxReturns.getTaxReturns({ company, ...filters }),
    enabled: Boolean(company),
  });

  const projects = data?.projects || [];
  const items = data?.items || [];
  const summary = data?.summary;
  // Paid or not can only be told from bank statements. A company we hold none
  // for sees what to pay and a payment slip instead of a status that would
  // always read „no statement".
  const hasStatements = (data?.statementCoverage?.length || 0) > 0;
  // Nothing imported for the company yet (as opposed to a filter matching nothing):
  // say so and what to do, instead of empty filters and a 0,00 total.
  const noReturns = Boolean(data) && !items.length && !Object.keys(filters).length;

  // Steps of the "How to use" box, with the icons in the order of the translated steps.
  const guideSteps = (key, icons, actions = []) => {
    // A key returned as text means the translations are not loaded yet.
    const steps = t(`howTo.${key}.steps`, { returnObjects: true });
    return (Array.isArray(steps) ? steps : []).map((step, i) => ({
      ...step,
      icon: icons[i],
      onClick: actions[i] || undefined,
    }));
  };
  // Clients see the same statuses, except that „awaiting entry" (our typing from a picture) reads as „in progress".
  const statusLabel = (st) => t(`status.${st}`, { context: canManage ? undefined : 'client' });
  const dueInText = (days) => (days === 0 ? t('dueSoon.today') : t('dueSoon.inDays', { count: days }));
  const dueSoonCount = data?.dueSoonCount || 0;
  // „Due soon" and a payment status are two ways of picking rows; one at a time.
  const showDueSoon = () => {
    setTab('returns');
    setFilters((f) => {
      const next = { ...f };
      delete next.status;
      if (f.dueSoon) delete next.dueSoon;
      else next.dueSoon = true;
      return next;
    });
  };
  const openImport = () => setImportOpen(true);
  // Overdue first: the rows the accountant has to chase.
  const showOverdue = () => {
    setTab('returns');
    setFilters((f) => ({ ...f, status: 'unpaid' }));
  };
  const guide = canManage
    ? [
        {
          title: t('howTo.pppd.title'),
          steps: guideSteps(
            'pppd',
            [FilePdfOutlined, BankOutlined, LinkOutlined, WarningOutlined, ClockCircleOutlined, EditOutlined],
            [openImport, openImport, () => setTab('accounts'), showOverdue, showDueSoon]
          ),
        },
        {
          title: t('howTo.pdpo.title'),
          steps: guideSteps('pdpo', [CodeOutlined, FilePdfOutlined, UploadOutlined, ExportOutlined], [null, null, openImport]),
        },
        {
          title: t('howTo.calc.title'),
          steps: guideSteps('calc', [CalculatorOutlined, CopyOutlined, FileDoneOutlined, QrcodeOutlined], [
            () => setTab('calculations'),
          ]),
        },
      ]
    : [
        {
          title: t('howTo.client.title'),
          // The status step only means something where statements are imported.
          steps: guideSteps('client', [EyeOutlined, ClockCircleOutlined, QrcodeOutlined, CheckCircleOutlined]).slice(
            0,
            hasStatements ? 4 : 3
          ),
        },
      ];

  const exportMutation = useMutation({
    mutationFn: () => {
      const labels = {
        sheet: t('export.sheet'),
        summarySheet: t('export.summarySheet'),
        project: t('fields.project'),
        title: t('fields.title'),
        category: t('fields.category'),
        type: t('fields.type'),
        period: t('fields.purpose'),
        bop: t('fields.bop'),
        filedAt: t('fields.filedAt'),
        dueDate: t('fields.dueDate'),
        amount: t('fields.amount'),
        paidAmount: t('fields.paidAmount'),
        lastPaidAt: t('fields.lastPaidAt'),
        status: t('fields.status'),
        note: t('fields.note'),
        count: t('summary.count'),
        open: t('summary.open'),
        overdueCount: t('summary.overdue'),
        dueSoonCount: t('dueSoon.card'),
        noProject: t('filters.noProject'),
        total: t('summary.total'),
        ...Object.fromEntries(STATUSES.map((s) => [`status_${s}`, statusLabel(s)])),
        ...Object.fromEntries(CATEGORIES.map((c) => [`category_${c}`, t(`category.${c}`)])),
      };
      return API.taxReturns.exportTaxReturns(company, filters, labels);
    },
    onSuccess: (blob) => saveBlob(blob, `${t('export.fileName')}.xlsx`),
  });

  const setFilter = (key, value) =>
    setFilters((f) => {
      const next = { ...f, [key]: value };
      if (value === undefined || value === null || value === '') delete next[key];
      if (key === 'status' && value) delete next.dueSoon;
      return next;
    });

  const years = useMemo(() => {
    const y = new Date().getFullYear();
    return [y + 1, y, y - 1, y - 2];
  }, []);

  const columns = [
    {
      title: t('fields.project'),
      dataIndex: 'project',
      width: 150,
      render: (p) => (p ? `${p.code} ${p.name}` : '—'),
    },
    {
      title: t('fields.title'),
      dataIndex: 'title',
      render: (v, r) => (
        <div>
          <div>{v || r.fileName}</div>
          <div className="text-xs opacity-60">
            {r.category && (
              <Tag color={CATEGORY_COLORS[r.category]} style={{ marginRight: 6 }}>
                {t(`category.${r.category}`)}
              </Tag>
            )}
            {r.bop || t('drawer.noBop')}
            {r.abExport && (
              <Tooltip title={t('ab.exportedTooltip', { date: date(r.abExport.at), by: r.abExport.byName || '—' })}>
                <Tag color="purple" style={{ marginLeft: 6 }}>
                  {t('ab.exportedTag')}
                </Tag>
              </Tooltip>
            )}
          </div>
        </div>
      ),
    },
    { title: t('fields.type'), dataIndex: 'type', width: 80 },
    {
      title: t('fields.purpose'),
      dataIndex: 'purpose',
      width: 110,
      // PDPO returns carry no PID; their period is the month the income was paid.
      render: (v, r) => v || (r.period?.month ? `${r.period.month}/${r.period.year}` : '—'),
    },
    { title: t('fields.filedAt'), dataIndex: 'filedAt', width: 105, render: date },
    {
      title: t('fields.dueDate'),
      dataIndex: 'dueDate',
      width: 120,
      render: (v, r) => (
        <div>
          <div>{date(v)}</div>
          {r.dueInDays !== null && r.dueInDays !== undefined && (
            <Tag color="orange" icon={<ClockCircleOutlined />} className="tax-return-due-tag">
              {dueInText(r.dueInDays)}
            </Tag>
          )}
        </div>
      ),
    },
    { title: t('fields.amount'), dataIndex: 'amount', width: 140, align: 'right', render: money },
    hasStatements && { title: t('fields.paidAmount'), dataIndex: 'paidAmount', width: 140, align: 'right', render: money },
    hasStatements && {
      title: t('fields.status'),
      dataIndex: 'status',
      width: 150,
      render: (s, r) => (
        <Space size={4}>
          <Tag color={STATUS_COLORS[s]}>{statusLabel(s)}</Tag>
          {r.override && (
            <Tooltip title={r.override.reason}>
              <EditOutlined />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: t('slip.column'),
      dataIndex: 'paymentSlip',
      width: 110,
      align: 'center',
      render: (slip, r) =>
        slip ? (
          <Button shape="round"
            size="small"
            icon={<QrcodeOutlined />}
            onClick={(e) => {
              // The row opens the details; this button only opens the slip.
              e.stopPropagation();
              setSlipOf(r);
            }}
          >
            {t('slip.button')}
          </Button>
        ) : null,
    },
  ].filter(Boolean);

  const statusCards = STATUSES.map((s) => ({ status: s, count: data?.statusCounts?.[s] || 0 })).filter(
    // Without statements only the returns still waiting for their data are worth a card.
    (c) => hasStatements || (c.status === 'pending_entry' && c.count > 0)
  );

  const returnsTab = noReturns ? (
    <Empty
      style={{ margin: '48px 0' }}
      description={
        <Space direction="vertical" size={4}>
          <span>{t('empty.title')}</span>
          <span className="text-sm opacity-70">{t(canManage ? 'empty.manage' : 'empty.client')}</span>
        </Space>
      }
    >
      {canManage && (
        <Button shape="round" type="primary" icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>
          {t('import.button')}
        </Button>
      )}
    </Empty>
  ) : (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space wrap>
        <Select
          allowClear
          style={{ width: 220 }}
          placeholder={t('filters.project')}
          value={filters.project}
          onChange={(v) => setFilter('project', v)}
          options={[
            ...projects.map((p) => ({ value: p._id, label: `${p.code} ${p.name}` })),
            { value: 'none', label: t('filters.noProject') },
          ]}
        />
        <Select
          allowClear
          style={{ width: 110 }}
          placeholder={t('filters.year')}
          value={filters.year}
          onChange={(v) => setFilter('year', v)}
          options={years.map((y) => ({ value: y, label: y }))}
        />
        <Select
          allowClear
          style={{ width: 110 }}
          placeholder={t('filters.month')}
          value={filters.month}
          onChange={(v) => setFilter('month', v)}
          options={MONTHS.map((m) => ({ value: m, label: m }))}
        />
        {hasStatements && (
        <Select
          allowClear
          style={{ width: 190 }}
          placeholder={t('filters.status')}
          value={filters.status}
          onChange={(v) => setFilter('status', v)}
          options={STATUSES.map((s) => ({ value: s, label: statusLabel(s) }))}
        />
        )}
        <Select
          allowClear
          style={{ width: 170 }}
          placeholder={t('filters.category')}
          value={filters.category}
          onChange={(v) => setFilter('category', v)}
          options={[
            ...CATEGORIES.map((c) => ({ value: c, label: t(`category.${c}`) })),
            { value: 'none', label: t('filters.noCategory') },
          ]}
        />
        <Select
          allowClear
          style={{ width: 120 }}
          placeholder={t('filters.type')}
          value={filters.type}
          onChange={(v) => setFilter('type', v)}
          options={['PPP-PD', 'PDPO'].map((v) => ({ value: v, label: v }))}
        />
        <Button shape="round" icon={<FileExcelOutlined />} loading={exportMutation.isPending} onClick={() => exportMutation.mutate()}>
          {t('export.button')}
        </Button>
        {canManage && (
          <Tooltip title={selected.length ? null : t('ab.selectHint')}>
            <Button shape="round" icon={<ExportOutlined />} disabled={!selected.length} onClick={() => setAbOpen(true)}>
              {t('ab.button', { count: selected.length })}
            </Button>
          </Tooltip>
        )}
        {canManage && !selected.length && items.some((r) => r.type === 'PDPO' && r.bop) && (
          <span className="text-xs text-zinc-500">{t('ab.selectInline')}</span>
        )}
      </Space>

      {dueSoonCount > 0 && !filters.dueSoon && (
        <Alert
          type="warning"
          showIcon
          icon={<ClockCircleOutlined className="tax-return-due-pulse" />}
          message={t(canManage ? 'dueSoon.alert' : 'dueSoon.alert_client', { count: dueSoonCount, days: data.dueSoonDays })}
          action={
            <Button shape="round" size="small" onClick={showDueSoon}>
              {t('dueSoon.show')}
            </Button>
          }
        />
      )}

      <Space wrap>
        {dueSoonCount > 0 && (
          <Card
            size="small"
            hoverable
            onClick={showDueSoon}
            style={{ minWidth: 130, borderColor: filters.dueSoon ? '#1677ff' : undefined }}
          >
            <Tag color="orange" icon={<ClockCircleOutlined />}>
              {t('dueSoon.card')}
            </Tag>
            <div className="mt-1 text-xl font-semibold">{dueSoonCount}</div>
          </Card>
        )}
        {statusCards.map(({ status, count }) => (
          <Card
            key={status}
            size="small"
            hoverable
            onClick={() => setFilter('status', filters.status === status ? null : status)}
            style={{
              minWidth: 130,
              borderColor: filters.status === status ? '#1677ff' : undefined,
            }}
          >
            <Tag color={STATUS_COLORS[status]}>{statusLabel(status)}</Tag>
            <div className="mt-1 text-xl font-semibold">{count}</div>
          </Card>
        ))}
        {summary && (
          <Card size="small" style={{ minWidth: 260 }}>
            <div className="text-xs opacity-70">{t('summary.amount')}</div>
            <div className="font-semibold">{money(summary.totals.amount)}</div>
            <div className="text-xs opacity-70">{t('summary.open')}</div>
            <div className="font-semibold">{money(summary.totals.open)}</div>
          </Card>
        )}
      </Space>

      {exportMutation.isError && <Alert type="error" showIcon message={errorText(t, exportMutation.error)} />}

      <Table
        size="small"
        rowKey="_id"
        loading={isLoading}
        dataSource={items}
        columns={columns}
        pagination={{ pageSize: 50, hideOnSinglePage: true }}
        rowClassName={(r) => (r.overdue ? 'tax-return-overdue' : r.dueInDays !== null ? 'tax-return-due-soon' : '')}
        rowSelection={
          canManage
            ? {
                selectedRowKeys: selected.map((r) => r._id),
                onChange: (_, rows) => setSelected(rows),
                // Only PDPO returns with a BOP can be booked: the BOP closes the liability when the tax is paid.
                getCheckboxProps: (r) => ({ disabled: r.type !== 'PDPO' || !r.bop }),
              }
            : undefined
        }
        onRow={(r) => ({ onClick: () => setOpenId(r._id), style: { cursor: 'pointer' } })}
      />

      {summary?.byProject?.length > 0 && (
        <Card size="small" title={t('summary.byProject')}>
          <Table
            size="small"
            rowKey={(g) => g.project?._id || 'none'}
            pagination={false}
            dataSource={summary.byProject}
            columns={[
              {
                title: t('fields.project'),
                dataIndex: 'project',
                render: (p) => (p ? `${p.code} ${p.name}` : t('filters.noProject')),
              },
              { title: t('summary.count'), dataIndex: 'count', align: 'right', width: 90 },
              { title: t('summary.amount'), dataIndex: 'amount', align: 'right', render: money },
              hasStatements && { title: t('fields.paidAmount'), dataIndex: 'paid', align: 'right', render: money },
              { title: t('summary.open'), dataIndex: 'open', align: 'right', render: money },
              hasStatements && {
                title: t('summary.overdue'),
                dataIndex: 'overdue',
                align: 'right',
                width: 110,
                render: (v) => (v ? <Tag color="red">{v}</Tag> : 0),
              },
              {
                title: t('dueSoon.card'),
                dataIndex: 'dueSoon',
                align: 'right',
                width: 120,
                render: (v) => (v ? <Tag color="orange">{v}</Tag> : 0),
              },
              hasStatements && {
                title: t('fields.status'),
                dataIndex: 'byStatus',
                render: (b) => (
                  <Space size={2} wrap>
                    {STATUSES.filter((s) => b[s]).map((s) => (
                      <Tag key={s} color={STATUS_COLORS[s]}>
                        {statusLabel(s)}: {b[s]}
                      </Tag>
                    ))}
                  </Space>
                ),
              },
            ].filter(Boolean)}
          />
        </Card>
      )}
    </Space>
  );

  return (
    <RootLayout metadata={{ title: t('taxReturns', { ns: 'menus' }) }}>
      <ContentBackground source="/assets/images/bg-pattern.svg" />
      <style>
        {[
          '.tax-return-overdue td { background: rgba(255, 77, 79, 0.12) !important; color: #cf1322; font-weight: 500; }',
          '.tax-return-due-soon td { background: rgba(250, 140, 22, 0.10) !important; }',
          // A slow pulse on the reminder, so an approaching due date catches the eye without shouting.
          '@keyframes tax-return-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }',
          '.tax-return-due-tag, .tax-return-due-pulse { animation: tax-return-pulse 1.6s ease-in-out infinite; }',
          '@media (prefers-reduced-motion: reduce) { .tax-return-due-tag, .tax-return-due-pulse { animation: none; } }',
        ].join('\n')}
      </style>

      <Section>
        <ContainerFluid className="foreground-wrapper">
          <ContentWrapper>
            <ContentHeader title={t('taxReturns', { ns: 'menus' })} />
            <ContentBody>
              <div className="with-y-overflow">
                <div className="p-4">
                  <p className="mb-4 text-sm opacity-70">{t(company && data && !hasStatements ? 'subtitleNoStatements' : 'subtitle')}</p>

                  <Space wrap style={{ marginBottom: 16 }}>
                    {!companies?.isSingle && (
                      <Select
                        showSearch
                        optionFilterProp="label"
                        style={{ width: 460 }}
                        popupMatchSelectWidth={false}
                        placeholder={t('filters.company')}
                        value={company}
                        onChange={(v) => {
                          setCompany(v);
                          setFilters({});
                          setSelected([]);
                        }}
                        options={companies?.options || []}
                      />
                    )}
                    {canManage && company && (
                      <Button shape="round" type="primary" icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>
                        {t('import.button')}
                      </Button>
                    )}
                  </Space>

                  {company && (
                    <HowToCard
                      title={t('howTo.title')}
                      sections={guide}
                      note={canManage ? t('howTo.note') : undefined}
                      open={data ? noReturns : undefined}
                    />
                  )}
                  {canManage && company && <DriveStatusLine company={company} onImported={() => refetch()} />}
                  {!company && <Alert type="info" showIcon message={t('pickCompany')} />}
                  {error && <Alert type="error" showIcon message={errorText(t, error)} />}

                  {company && (
                    <Tabs
                      activeKey={tab}
                      onChange={setTab}
                      items={[
                        { key: 'returns', label: t('tabs.returns'), children: returnsTab },
                        {
                          key: 'calculations',
                          label: t('tabs.calculations'),
                          children: (
                            <CalculationsPanel
                              company={company}
                              projects={projects}
                              canManage={canManage}
                              onOpenReturn={setOpenId}
                            />
                          ),
                        },
                        // Statements are HLB's to import; a client without any has nothing to see there.
                        (canManage || hasStatements) && {
                          key: 'accounts',
                          label: t('tabs.accounts'),
                          children: <AccountsPanel company={company} projects={projects} canManage={canManage} />,
                        },
                      ].filter(Boolean)}
                    />
                  )}
                </div>
              </div>
            </ContentBody>
          </ContentWrapper>
        </ContainerFluid>
      </Section>

      <ImportModal
        open={importOpen}
        company={company}
        projects={projects}
        onClose={() => setImportOpen(false)}
        onImported={() => refetch()}
      />
      <PaymentSlipModal
        slip={slipOf?.paymentSlip}
        title={slipOf ? `${t('slip.title')}: ${slipOf.title || slipOf.bop}` : ''}
        onClose={() => setSlipOf(null)}
      />
      <AbJournalModal
        open={abOpen}
        company={company}
        returns={selected}
        onClose={() => setAbOpen(false)}
        onExported={() => {
          setSelected([]);
          refetch();
        }}
      />
      <ReturnDrawer
        company={company}
        hasStatements={hasStatements}
        onSlip={setSlipOf}
        id={openId}
        projects={projects}
        canManage={canManage}
        onClose={() => setOpenId(null)}
        onChanged={() => refetch()}
      />
    </RootLayout>
  );
}

const TaxReturnsPage = withPrivateRoute(TaxReturnsContent, {
  ...ROLES_PERMISSIONS.TAX_RETURNS.VIEW,
  requiredCompanyFlag: 'hasTaxReturns',
});
TaxReturnsPage.displayName = 'TaxReturnsPage';

export default TaxReturnsPage;
